import logger from '../utils/logger';
import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import api from '../config/api';
import { useAuth } from './AuthContext';

const ShopContext = createContext();

export const useShop = () => {
  const context = useContext(ShopContext);
  if (!context) {
    throw new Error('useShop must be used within a ShopProvider');
  }
  return context;
};

export const ShopProvider = ({ children }) => {
  const { user } = useAuth();
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialized] = useState(true);
  const hasAttemptedFetch = useRef(false);
  
  // Request deduplication refs
  const shopRequestRef = useRef(null);
  const productsRequestRef = useRef(null);
  const tablesRequestRef = useRef(null);

  const fetchShop = useCallback(async () => {
    // Don't fetch shop data for admin users
    const isAdminUser = user && (
      user.role === 'ADMIN' || 
      user.authorities?.includes('ADMIN') ||
      user.email === 'admin@coffeecloud.com'
    );
    
    if (isAdminUser) {
  logger.debug('ShopContext: Skipping shop fetch - user is admin');
      return null;
    }

    if (hasAttemptedFetch.current) {
  logger.debug('ShopContext: Skipping fetch - already attempted');
      return;
    }

    // If there's already a request in progress, return the existing promise
    if (shopRequestRef.current) {
  logger.debug('ShopContext: Returning existing shop request');
      return shopRequestRef.current;
    }

  logger.debug('ShopContext: Starting shop fetch...');

    try {
      setLoading(true);
      setError(null);
      hasAttemptedFetch.current = true;
  logger.debug('ShopContext: Fetching shop data...');
      
      // Create the request promise and store it
      shopRequestRef.current = api.get('/auth/me/shop');
      
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), 10000);
      });
      
      const response = await Promise.race([shopRequestRef.current, timeoutPromise]);
      
  logger.debug('ShopContext: Shop data received:', response.data);
      setShop(response.data);
      
      // Automatically fetch products and tables when shop is loaded
      if (response.data) {
  logger.debug('ShopContext: Auto-fetching products and tables for shop');
        // Fetch both in parallel
        Promise.all([
          fetchProductsInternal(),
          fetchTablesInternal()
        ]).catch(error => {
          logger.error('ShopContext: Error fetching products/tables:', error);
        });
      }
      
      return response.data;
    } catch (error) {
  logger.error('ShopContext: Failed to fetch shop:', error);
  logger.error('ShopContext: Error response:', error.response?.data);
  logger.error('ShopContext: Error status:', error.response?.status);
      setError(error);
      setShop(null);
      throw error;
    } finally {
      setLoading(false);
      shopRequestRef.current = null; // Clear the request ref
    }
  }, [user]);

  // Internal fetch functions that don't trigger loading state
  const fetchProductsInternal = async () => {
    if (productsRequestRef.current) {
      return productsRequestRef.current;
    }

    try {
      productsRequestRef.current = api.get('/shop/products');
      const response = await productsRequestRef.current;
      setProducts(response.data);
      return response.data;
    } catch (error) {
  logger.error('Failed to fetch products:', error);
      throw error;
    } finally {
      productsRequestRef.current = null;
    }
  };

  const fetchTablesInternal = async () => {
    if (tablesRequestRef.current) {
      return tablesRequestRef.current;
    }

    try {
      tablesRequestRef.current = api.get('/shop/tables');
      const response = await tablesRequestRef.current;
      setTables(response.data);
      return response.data;
    } catch (error) {
  logger.error('Failed to fetch tables:', error);
      throw error;
    } finally {
      tablesRequestRef.current = null;
    }
  };

  const createShop = useCallback(async (shopData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.post('/shop', shopData);
      setShop(response.data);
      hasAttemptedFetch.current = true;
      return { success: true };
    } catch (error) {
      setError(error);
      return { 
        success: false, 
        error: error.response?.data?.message || 'Failed to create shop' 
      };
    } finally {
      setLoading(false);
    }
  }, []);

  const createProduct = useCallback(async (productData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.post('/shop/products', productData);
      setProducts(prev => [...prev, response.data]);
      return { success: true };
    } catch (error) {
      // Distinguish subscription/payment requirement (HTTP 402) from real connection/shop errors
      const status = error.response?.status;
      if (status === 402) {
        // Do NOT set global context error (would trigger full-screen connection error)
        return {
          success: false,
            code: 'SUBSCRIPTION_REQUIRED',
          error: error.response?.data?.message || 'Subscription required to create products'
        };
      }
      setError(error); // non-402: treat as real error
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to create product'
      };
    } finally {
      setLoading(false);
    }
  }, []);

  const updateProduct = useCallback(async (productId, productData) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.put(`/shop/products/${productId}`, productData);
      setProducts(prev => prev.map(p => p.id === productId ? response.data : p));
      return { success: true };
    } catch (error) {
      setError(error);
      return { 
        success: false, 
        error: error.response?.data?.message || 'Failed to update product' 
      };
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteProduct = useCallback(async (productId) => {
    try {
      setLoading(true);
      setError(null);
      await api.delete(`/shop/products/${productId}`);
      setProducts(prev => prev.filter(p => p.id !== productId));
      return { success: true };
    } catch (error) {
      setError(error);
      return { 
        success: false, 
        error: error.response?.data?.message || 'Failed to delete product' 
      };
    } finally {
      setLoading(false);
    }
  }, []);

  const createTables = useCallback(async (tableCount) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.post('/shop/tables', { count: tableCount });
      setTables(response.data);
      return { success: true };
    } catch (error) {
      setError(error);
      return { 
        success: false, 
        error: error.response?.data?.message || 'Failed to create tables' 
      };
    } finally {
      setLoading(false);
    }
  }, []);

  const resetShopState = useCallback(() => {
    setShop(null);
    setProducts([]);
    setTables([]);
    setError(null);
    hasAttemptedFetch.current = false;
    // Clear all request refs
    shopRequestRef.current = null;
    productsRequestRef.current = null;
    tablesRequestRef.current = null;
  }, []);



  const value = {
    shop,
    products,
    tables,
    loading,
    error,
    initialized,
    fetchShop,
    createShop,
    fetchProducts: fetchProductsInternal, // Expose the internal function
    createProduct,
    updateProduct,
    deleteProduct,
    fetchTables: fetchTablesInternal, // Expose the internal function
    createTables,
    resetShopState,
    hasShop: !!shop
  };

  return (
    <ShopContext.Provider value={value}>
      {children}
    </ShopContext.Provider>
  );
}; 